/* eslint-disable @typescript-eslint/naming-convention -- Features API operator values are uppercase with underscores */
export enum Op {
  EQ = 'EQ',
  IN = 'IN',
  OVERLAPS = 'OVERLAPS',
  NOTEXISTS = 'NOTEXISTS',
  EXISTS = 'EXISTS',
  LT = 'LT',
  LTEQ = 'LTEQ',
  GT = 'GT',
  GTEQ = 'GTEQ',
  NE = 'NE',
  LIKE = 'LIKE',
  CONTAINED_IN = 'CONTAINED_IN',
  NOTIN = 'NOTIN',
  NOTCONTAINED_IN = 'NOTCONTAINED_IN',
  NOTLIKE = 'NOTLIKE',
}
/* eslint-enable @typescript-eslint/naming-convention */

export type FilterOperand = string | boolean | readonly string[];

export interface FilterClause {
  readonly operator: Op;
  readonly field: string;
  readonly operand: string | boolean | string[];
}

export interface FilterOrGroup {
  readonly or: FilterClause[];
}

export type FilterExpression = FilterClause | FilterOrGroup;

const normalizeOperand = (
  value: FilterOperand
  // eslint-disable-next-line sonarjs/function-return-type -- union return type is intentional
): string | boolean | string[] => {
  if (typeof value === 'string' || typeof value === 'boolean') {
    return value;
  }

  if (value.length === 1) {
    return value[0];
  }

  return [...value];
};

/**
 * Builds one Features API filter clause. EQ with multiple values becomes IN.
 */
export const createFilter = (
  operator: Op,
  field: string,
  value: FilterOperand
): FilterClause => {
  const normalizedOperator =
    operator === Op.EQ && Array.isArray(value) && value.length > 1
      ? Op.IN
      : operator;

  return {
    operator: normalizedOperator,
    field,
    operand: normalizeOperand(value),
  };
};

export const stringifyFilterClauses = (
  clauses: readonly FilterExpression[]
): string => JSON.stringify(clauses);

/**
 * IN filter on a field. Empty values yield no clause.
 */
export const createInFilter = (
  field: string,
  values: readonly string[]
): FilterClause | undefined => {
  if (values.length === 0) {
    return undefined;
  }

  return createFilter(Op.IN, field, [...values]);
};

export const createFeaturesIdInFilterClause = (
  featureIds: readonly string[]
): string => {
  const clause = createInFilter('id', featureIds);
  return stringifyFilterClauses(clause ? [clause] : []);
};
